/**
 * 55 — the player.
 *
 * ONE <audio> element, created once, alive for the life of the tab.
 *
 * Why an <audio> element and not Web Audio — three reasons, any one of them
 * fatal on its own:
 *
 *   1. The iOS silent switch mutes Web Audio. It does not mute <audio>. If
 *      Rob's ringer switch is off, a Web Audio player is silently dead, with
 *      no error and no way for him to know. He concludes the app is broken and
 *      never opens it again.
 *   2. Backgrounding the tab suspends an AudioContext. <audio> keeps playing.
 *   3. The Media Session API only works with a media element — and Media
 *      Session is what puts the song on the lock screen, which is the entire
 *      difference between "a website" and "an app" for someone who is not
 *      going to be impressed by any of this.
 *
 * Why ONE element and not one per track: iOS 26 has known background-audio
 * bugs in installed PWAs (works the first session, breaks in later ones). A
 * single long-lived element, with the Media Session handlers re-armed on
 * visibilitychange, is the configuration that survives it.
 */

import { getStream, ApiError, type StreamInfo } from './api';

/* --------------------------------------------------------------- the element */

const audio = new Audio();
audio.preload = 'metadata';
// NOT crossOrigin='anonymous'. We never read the audio's samples (peaks come
// pre-computed from R2), so we don't need CORS on the media fetch — and asking
// for it would make playback depend on R2's CORS config being exactly right.
// A media element can play cross-origin bytes without any of that.
audio.setAttribute('playsinline', '');
audio.setAttribute('webkit-playsinline', '');

/** Handy in the console; genuinely useful when debugging on a real phone. */
(window as unknown as { __audio?: HTMLAudioElement }).__audio = audio;

export function element(): HTMLAudioElement {
  return audio;
}

/* -------------------------------------------------------------------- state */

export interface NowPlaying {
  versionId: string;
  songId: string;
  /** the take behind this version — lets the footer fetch its synced lyrics */
  takeId?: string;
  /** the song */
  title: string;
  /** the version — "Apr 4 full band" */
  subtitle: string;
  /** the song's colour, for the vinyl label */
  color: string;
  /** which Tier-1 mix preset we're on, if any */
  presetId: string | null;
}

/** One entry in the play queue. Enough to paint the now-playing bar + mint a URL. */
export interface QueueItem {
  songId: string;
  versionId: string;
  takeId?: string;
  title: string;
  subtitle: string;
  color: string;
}

/** Repeat behaviour for the queue. Persisted in localStorage ('55.loop'). */
export type LoopMode = 'off' | 'one' | 'all';

const LOOP_KEY = '55.loop';

function loadLoop(): LoopMode {
  try {
    const v = localStorage.getItem(LOOP_KEY);
    if (v === 'off' || v === 'one' || v === 'all') return v;
  } catch {
    /* private mode / storage disabled — fall back to 'off' */
  }
  return 'off';
}

let loopMode: LoopMode = loadLoop();

export interface PlayerState {
  now: NowPlaying | null;
  playing: boolean;
  /** fetching a URL, or waiting on the network mid-track */
  loading: boolean;
  duration: number;
  currentTime: number;
  /** in human words, or null. Never a status code. */
  error: string | null;
  /** iOS refused an autoplay: we need a real tap. */
  needsTap: boolean;
  /** how many items are in the active queue (0 = single track / no queue). */
  queueLength: number;
  /** position within the queue, or -1 when there is no queue. */
  queueIndex: number;
  /** the current repeat mode. */
  loop: LoopMode;
}

const state: PlayerState = {
  now: null,
  playing: false,
  loading: false,
  duration: 0,
  currentTime: 0,
  error: null,
  needsTap: false,
  queueLength: 0,
  queueIndex: -1,
  loop: loopMode,
};

export function get(): Readonly<PlayerState> {
  return state;
}

/* ------------------------------------------------------------------- queue */

/**
 * The play queue. Empty by default — a bare play() is a "queue of one" that
 * ignores all of this. setQueue() fills it and continuous playback + loop
 * modes kick in. We never prefetch the whole album's URLs: each item's stream
 * URL is minted (through the same per-version cache) only when it goes current.
 */
let queue: QueueItem[] = [];
let queueIndex = -1;

/**
 * A standalone play() clears the queue so a lone tap on the shelf/song screen
 * doesn't keep auto-advancing an old album. Queue navigation (setQueue / next /
 * prev / auto-advance) sets this first so its own play() call is spared that
 * reset. It is consumed on the very first line of play(), so it can't leak.
 */
let suppressQueueClear = false;

/* ---------------------------------------------------------------- listeners */

type Fn = () => void;

/** Coarse changes: track, play/pause, loading, error. Cheap to handle. */
const listeners = new Set<Fn>();
/** Per-frame time updates. Kept separate so a render() never runs at 60fps. */
const tickers = new Set<(t: number, d: number) => void>();

export function subscribe(fn: Fn): Fn {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function onTick(fn: (t: number, d: number) => void): Fn {
  tickers.add(fn);
  return () => tickers.delete(fn);
}

function emit() {
  // Keep the queue mirror on state fresh for every listener — cheap, and it
  // means the UI reads queue length/index/loop straight off player.get().
  state.queueLength = queue.length;
  state.queueIndex = queueIndex;
  state.loop = loopMode;
  for (const fn of listeners) fn();
}

let rafId = 0;
function tickLoop() {
  const t = audio.currentTime;
  const d = Number.isFinite(audio.duration) ? audio.duration : state.duration;
  state.currentTime = t;
  if (d) state.duration = d;
  for (const fn of tickers) fn(t, d);
  rafId = requestAnimationFrame(tickLoop);
}

function startTicking() {
  if (!rafId) rafId = requestAnimationFrame(tickLoop);
}
function stopTicking() {
  if (rafId) cancelAnimationFrame(rafId);
  rafId = 0;
  // one last pass so the UI lands on the true final position
  state.currentTime = audio.currentTime;
  for (const fn of tickers) fn(state.currentTime, state.duration);
}

/* ------------------------------------------------- the presigned-URL cache */

/**
 * Presigned R2 URLs are good for an hour. We keep them for 55 minutes and then
 * silently mint a new one — the five-minute margin covers a track that starts
 * just before the deadline, plus Safari's habit of firing a fresh range
 * request minutes into playback.
 *
 * Do NOT call the stream endpoint on every seek. Seeking inside an already-
 * loaded media element costs nothing; re-minting costs a Worker invocation and
 * a round trip on a bad connection.
 */
const TTL_MS = 55 * 60 * 1000;

interface Cached extends StreamInfo {
  fetchedAt: number;
}

const urlCache = new Map<string, Cached>();

function cached(versionId: string): Cached | null {
  const hit = urlCache.get(versionId);
  if (!hit) return null;
  if (Date.now() - hit.fetchedAt > TTL_MS) {
    urlCache.delete(versionId);
    return null;
  }
  return hit;
}

async function mint(versionId: string, force = false): Promise<Cached> {
  if (!force) {
    const hit = cached(versionId);
    if (hit) return hit;
  }
  const info = await getStream(versionId);
  const rec: Cached = { ...info, fetchedAt: Date.now() };
  urlCache.set(versionId, rec);
  return rec;
}

/** The song screen wants the peaks URL without starting playback. */
export async function streamInfo(versionId: string): Promise<StreamInfo> {
  return mint(versionId);
}

/* --------------------------------------------------- iOS: the gesture lock */

/**
 * Safari will only let an <audio> element play if the FIRST play() came from
 * inside a user gesture. Our flow is "tap a record → await a presigned URL →
 * play", and that await severs the gesture: by the time the URL lands, the tap
 * is over and Safari says no.
 *
 * The fix is to touch the element *synchronously* during the tap — play 76
 * bytes of silence. That unlocks the element for the rest of the page's life,
 * and every later programmatic play() is allowed.
 *
 * This is why `unlock()` is wired to a capture-phase pointerdown as well as
 * being called from play(): whichever tap comes first, the element is hot
 * before we ever need it.
 */
const SILENCE =
  'data:audio/wav;base64,UklGRkQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YSAAAACAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgA==';

let unlocked = false;

function unlock(): void {
  if (unlocked) return;
  unlocked = true;
  try {
    audio.src = SILENCE;
    audio.load();
    const p = audio.play();
    if (p) p.then(() => audio.pause()).catch(() => undefined);
  } catch {
    /* if this fails we're no worse off than we were */
  }
}

document.addEventListener('pointerdown', unlock, { capture: true, once: true });
document.addEventListener('touchend', unlock, { capture: true, once: true });

/* --------------------------------------------------------------- playback */

/** Guards the recovery path against an infinite re-mint loop. */
let recoverAttempts = 0;
/** Bumped on every play()/switch so a slow response can't clobber a new track. */
let generation = 0;

export interface PlayRequest {
  versionId: string;
  songId: string;
  takeId?: string;
  title: string;
  subtitle: string;
  color?: string;
  /** start here (seconds) */
  seekTo?: number;
}

/**
 * Play a version. Tapping a record on the shelf calls exactly this — it is not
 * a menu, it plays.
 */
export async function play(req: PlayRequest): Promise<void> {
  // A standalone play() takes over from any queue; queue navigation suppresses
  // this so its own play() call keeps the list intact.
  if (suppressQueueClear) suppressQueueClear = false;
  else clearQueue();

  unlock();

  // Same track, just paused → resume. No network, no reload, no gap.
  if (state.now?.versionId === req.versionId && audio.src && !audio.src.startsWith('data:')) {
    await resume();
    return;
  }

  const gen = ++generation;
  recoverAttempts = 0;

  state.now = {
    versionId: req.versionId,
    songId: req.songId,
    takeId: req.takeId,
    title: req.title,
    subtitle: req.subtitle,
    color: req.color || '#d8241a',
    presetId: null,
  };
  state.loading = true;
  state.error = null;
  state.needsTap = false;
  state.currentTime = 0;
  state.duration = 0;
  emit();

  let info: Cached;
  try {
    info = await mint(req.versionId);
  } catch (e) {
    if (gen !== generation) return;
    state.loading = false;
    state.playing = false;
    state.error = e instanceof ApiError ? e.human : 'Couldn’t reach the vault. Try again?';
    emit();
    return;
  }

  if (gen !== generation) return; // they tapped something else while we waited

  if (info.duration_s) state.duration = info.duration_s;

  await load(info.url, req.seekTo ?? 0);
  setMediaSession();
}

/** Point the element at a URL and go. Shared by play() and the recovery path. */
async function load(url: string, seekTo: number): Promise<void> {
  audio.src = url;
  audio.load();

  if (seekTo > 0) {
    // currentTime can't be set until we have metadata to set it against.
    const seek = () => {
      try {
        audio.currentTime = seekTo;
      } catch {
        /* some browsers throw if the media isn't seekable yet; the
           loadedmetadata handler below will get another crack at it */
      }
    };
    if (audio.readyState >= 1) seek();
    else audio.addEventListener('loadedmetadata', seek, { once: true });
  }

  try {
    await audio.play();
    state.needsTap = false;
  } catch (e) {
    // NotAllowedError = Safari wants a real finger. Say so, don't fail silently:
    // a play button that does nothing is how you lose someone forever.
    const name = (e as Error)?.name;
    if (name === 'NotAllowedError') {
      state.needsTap = true;
      state.playing = false;
      state.loading = false;
      emit();
    } else if (name !== 'AbortError') {
      // AbortError just means the src changed under us — that's us, not a fault.
      state.error = 'Couldn’t start that one. Try again?';
      state.loading = false;
      emit();
    }
  }
}

export async function resume(): Promise<void> {
  unlock();
  try {
    await audio.play();
    state.needsTap = false;
  } catch (e) {
    if ((e as Error)?.name === 'NotAllowedError') {
      state.needsTap = true;
      emit();
    }
  }
}

export function pause(): void {
  audio.pause();
}

export function toggle(): void {
  if (audio.paused) void resume();
  else pause();
}

export function seek(t: number): void {
  const d = state.duration || audio.duration;
  const clamped = Math.max(0, Math.min(Number.isFinite(d) && d > 0 ? d : t, t));
  try {
    audio.currentTime = clamped;
  } catch {
    /* not seekable yet — harmless, the user will try again */
  }
  state.currentTime = clamped;
  for (const fn of tickers) fn(clamped, state.duration);
  updatePositionState();
}

export function isCurrent(versionId: string): boolean {
  return state.now?.versionId === versionId;
}

export function stop(): void {
  generation++;
  clearQueue();
  audio.pause();
  audio.removeAttribute('src');
  audio.load();
  state.now = null;
  state.playing = false;
  state.currentTime = 0;
  state.duration = 0;
  emit();
}

/* ------------------------------------------------------------ the play queue */

/**
 * Load a queue and start at startIndex. This is how the album plays end to end:
 * hand the whole tracklist here with startIndex on the tapped track, and it
 * plays song-to-song. A bare play() anywhere else still works and just clears
 * this. Returns the promise of the first track's play() so callers can await
 * the initial load if they want to.
 */
export function setQueue(items: QueueItem[], startIndex = 0): Promise<void> {
  if (items.length === 0) {
    clearQueue();
    emit();
    return Promise.resolve();
  }
  queue = items.slice();
  const idx = Math.max(0, Math.min(items.length - 1, startIndex | 0));
  return playAt(idx);
}

/** The current repeat mode. */
export function getLoop(): LoopMode {
  return loopMode;
}

/** Set + persist the repeat mode ('off' | 'one' | 'all'). */
export function setLoop(mode: LoopMode): void {
  loopMode = mode;
  try {
    localStorage.setItem(LOOP_KEY, mode);
  } catch {
    /* storage disabled — the mode still holds for this session */
  }
  emit();
}

/**
 * Skip forward. Loop 'all' wraps past the end; loop 'off' at the last track is
 * a no-op. Loop 'one' does NOT trap a manual skip — it only repeats on a
 * natural end, which is what people expect from a repeat-one button.
 */
export function next(): void {
  if (queue.length === 0) return;
  const n = queueIndex + 1;
  if (n < queue.length) void playAt(n);
  else if (loopMode === 'all') void playAt(0);
}

/**
 * Standard "previous": more than ~3s into a track, restart it; otherwise step
 * back one (wrapping to the last track under loop 'all', restarting the first
 * track otherwise).
 */
export function prev(): void {
  if (queue.length === 0) return;
  if (audio.currentTime > 3) {
    seek(0);
    return;
  }
  const p = queueIndex - 1;
  if (p >= 0) void playAt(p);
  else if (loopMode === 'all') void playAt(queue.length - 1);
  else seek(0);
}

/** Play queue[index], keeping the queue intact. The URL is minted on demand. */
async function playAt(index: number): Promise<void> {
  const item = queue[index];
  if (!item) return;
  queueIndex = index;
  suppressQueueClear = true;
  await play({
    versionId: item.versionId,
    songId: item.songId,
    takeId: item.takeId,
    title: item.title,
    subtitle: item.subtitle,
    color: item.color,
    seekTo: 0,
  });
}

/** Reset to the no-queue state. Callers emit() when the surrounding op does. */
function clearQueue(): void {
  queue = [];
  queueIndex = -1;
}

/**
 * The 'ended' auto-advance for an active queue. Loop 'one' is handled earlier
 * (it repeats whether or not there's a queue). Here: move to the next item,
 * wrap under 'all', or fall through to the old stop under 'off' at the end.
 */
async function advanceQueue(): Promise<void> {
  const n = queueIndex + 1;
  if (n < queue.length) {
    await playAt(n);
    return;
  }
  if (loopMode === 'all' && queue.length > 0) {
    await playAt(0);
    return;
  }
  endStop();
}

/** Repeat the current track from the top (loop 'one', or a natural single-track end). */
function replayCurrent(): void {
  try {
    audio.currentTime = 0;
  } catch {
    /* not seekable yet — resume() still restarts it from wherever it is */
  }
  state.currentTime = 0;
  void resume();
}

/** The end-of-everything stop: exactly what 'ended' did before queues existed. */
function endStop(): void {
  state.playing = false;
  state.currentTime = state.duration;
  stopTicking();
  setPlaybackState('paused');
  emit();
}

/* ------------------------------------------- Tier 1: switching a mix preset */

/**
 * The layer feature that actually works on every phone ever made
 * (12_STEMS_AND_LAYERS Part 3): the Roadie pre-renders each mix as an ordinary
 * MP3, and we just point the same <audio> element at a different one.
 *
 * Zero memory. Zero drift. Zero sync risk. Lock-screen controls keep working.
 *
 * The offsets matter: a "-brad only" render is a separate bounce with a
 * different trim — the real files are 8 to 13 seconds off the full mix. So
 * position is translated, not copied:
 *
 *     new.currentTime = old.currentTime + (offset_new - offset_old)
 *
 * which is what makes "flip to brad-only right at the chorus" land on the
 * chorus, instead of somewhere in the second verse.
 */
export async function switchPreset(
  presetId: string | null,
  url: string,
  offsetNew: number,
  offsetOld: number,
): Promise<void> {
  if (!state.now) return;

  const wasPlaying = !audio.paused;
  const t = audio.currentTime;
  const target = Math.max(0, t + (offsetNew - offsetOld));

  await fadeOut();

  audio.src = url;
  audio.load();

  const restore = () => {
    try {
      audio.currentTime = target;
    } catch {
      /* ignore */
    }
  };
  if (audio.readyState >= 1) restore();
  else audio.addEventListener('loadedmetadata', restore, { once: true });

  state.now.presetId = presetId;
  emit();

  if (wasPlaying) {
    try {
      await audio.play();
    } catch {
      state.needsTap = true;
      emit();
    }
  }
  fadeIn();
}

/**
 * A 20 ms volume ramp so the swap is a fade, not a click.
 *
 * On iOS, HTMLMediaElement.volume is read-only — the hardware buttons own it.
 * So on an iPhone this degrades to a straight cut at the same position, which
 * is fine. It costs us nothing to do it properly everywhere else.
 */
const FADE_MS = 20;

function fadeOut(): Promise<void> {
  return new Promise((res) => {
    const from = audio.volume;
    if (from === 0) return res();
    const t0 = performance.now();
    const step = () => {
      const k = Math.min(1, (performance.now() - t0) / FADE_MS);
      audio.volume = from * (1 - k);
      if (k < 1) requestAnimationFrame(step);
      else res();
    };
    step();
  });
}

function fadeIn(): void {
  const t0 = performance.now();
  const step = () => {
    const k = Math.min(1, (performance.now() - t0) / FADE_MS);
    audio.volume = k;
    if (k < 1) requestAnimationFrame(step);
  };
  step();
}

/* ------------------------------------------------------- error & recovery */

/**
 * The presigned URL expired mid-playback (or R2 said 403, or the phone dropped
 * off the network for a moment). A media element gives us no status code — all
 * we get is MEDIA_ERR_NETWORK or MEDIA_ERR_SRC_NOT_SUPPORTED — so we treat any
 * network-shaped failure the same way:
 *
 *   remember where we were → mint a fresh URL → reload → seek back → keep going.
 *
 * Done fast enough, the listener hears a hiccup and nothing else. What they
 * must never see is a dead player and the word "403".
 */
audio.addEventListener('error', () => {
  const now = state.now;
  if (!now) return;
  if (!audio.src || audio.src.startsWith('data:')) return; // the silence primer

  const code = audio.error?.code ?? 0;
  const recoverable =
    code === MediaError.MEDIA_ERR_NETWORK || code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED;

  if (!recoverable || recoverAttempts >= 2) {
    state.playing = false;
    state.loading = false;
    state.error = navigator.onLine
      ? 'That one stopped. Try it again?'
      : "You're offline. This one isn't saved on your phone.";
    emit();
    return;
  }

  recoverAttempts++;
  void recover(now.versionId, audio.currentTime);
});

async function recover(versionId: string, at: number): Promise<void> {
  const gen = generation;
  state.loading = true;
  emit();

  try {
    const fresh = await mint(versionId, /* force */ true); // bypass the cache — the cached one is the broken one
    if (gen !== generation || state.now?.versionId !== versionId) return;
    await load(fresh.url, at);
  } catch {
    if (gen !== generation) return;
    state.loading = false;
    state.playing = false;
    state.error = navigator.onLine
      ? 'Couldn’t reach the vault. Try again?'
      : "You're offline. Nothing's broken — you just can't reach the vault right now.";
    emit();
  }
}

/* ------------------------------------------------------------ element wiring */

audio.addEventListener('playing', () => {
  state.playing = true;
  state.loading = false;
  state.error = null;
  state.needsTap = false;
  recoverAttempts = 0; // we're fine again; the budget resets
  startTicking();
  setPlaybackState('playing');
  emit();
});

audio.addEventListener('pause', () => {
  state.playing = false;
  stopTicking();
  setPlaybackState('paused');
  emit();
});

audio.addEventListener('waiting', () => {
  state.loading = true;
  emit();
});

audio.addEventListener('canplay', () => {
  if (state.loading) {
    state.loading = false;
    emit();
  }
});

audio.addEventListener('loadedmetadata', () => {
  if (Number.isFinite(audio.duration)) {
    state.duration = audio.duration;
    emit();
    updatePositionState();
  }
});

audio.addEventListener('durationchange', () => {
  if (Number.isFinite(audio.duration)) state.duration = audio.duration;
});

audio.addEventListener('seeked', updatePositionState);

audio.addEventListener('ended', () => {
  // Loop 'one' repeats the current track whether or not a queue is active.
  if (loopMode === 'one' && state.now) {
    replayCurrent();
    return;
  }
  // A live queue auto-advances (next / wrap / stop-at-end) instead of stopping.
  if (queue.length > 0) {
    void advanceQueue();
    return;
  }
  // No queue, no loop: the original single-track behaviour, untouched.
  endStop();
});

/* --------------------------------------------------------- Media Session */

/**
 * The lock screen. This is the feature that makes someone believe the thing on
 * their phone is a real app: they hit play, put the phone in their pocket, and
 * the song is on the lock screen with a proper transport.
 */
const ART = [
  { src: '/icon-512.png', sizes: '512x512', type: 'image/png' },
  // 512 only. iOS renders a smaller artwork blurry and it looks cheap.
];

function setMediaSession(): void {
  if (!('mediaSession' in navigator)) return;
  const now = state.now;
  if (!now) return;

  navigator.mediaSession.metadata = new MediaMetadata({
    title: now.title,
    artist: '55',
    album: now.subtitle,
    artwork: ART,
  });

  armHandlers();
}

function armHandlers(): void {
  if (!('mediaSession' in navigator)) return;
  const ms = navigator.mediaSession;
  const safe = (
    action: MediaSessionAction,
    handler: MediaSessionActionHandler | null,
  ) => {
    try {
      ms.setActionHandler(action, handler);
    } catch {
      // Not every browser supports every action; an unsupported one throws.
      // That's fine — it just means no button for it.
    }
  };

  safe('play', () => void resume());
  safe('pause', () => pause());
  safe('seekto', (d) => {
    if (typeof d.seekTime === 'number') seek(d.seekTime);
  });
  safe('seekbackward', (d) => seek(audio.currentTime - (d.seekOffset ?? 10)));
  safe('seekforward', (d) => seek(audio.currentTime + (d.seekOffset ?? 10)));
  safe('stop', () => stop());
  // Next/previous appear only while a queue is active (album playback). A lone
  // track has no queue, so these stay null and the lock-screen buttons hide —
  // re-armed every setMediaSession()/visibilitychange, so they track the queue.
  const hasQueue = queue.length > 0;
  safe('nexttrack', hasQueue ? () => next() : null);
  safe('previoustrack', hasQueue ? () => prev() : null);
}

function setPlaybackState(s: MediaSessionPlaybackState): void {
  if ('mediaSession' in navigator) navigator.mediaSession.playbackState = s;
}

function updatePositionState(): void {
  if (!('mediaSession' in navigator) || !navigator.mediaSession.setPositionState) return;
  const d = audio.duration;
  if (!Number.isFinite(d) || d <= 0) return;
  try {
    navigator.mediaSession.setPositionState({
      duration: d,
      playbackRate: audio.playbackRate || 1,
      position: Math.min(audio.currentTime, d),
    });
  } catch {
    /* Safari throws if position > duration by a hair. Not worth caring about. */
  }
}

/**
 * iOS 26 loses the Media Session handlers on an installed PWA after the app
 * has been backgrounded — the audio keeps going but the lock-screen buttons go
 * dead, which looks exactly like a crash. Re-arming them every time we come
 * back to the foreground costs nothing and papers over the bug.
 */
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && state.now) {
    armHandlers();
    setPlaybackState(state.playing ? 'playing' : 'paused');
    updatePositionState();

    // A URL we minted 50 minutes ago is about to stop working. Refresh it now,
    // quietly, while they're looking at the shelf — not at the moment they hit
    // play. The user should never find out this happened.
    const v = state.now.versionId;
    const hit = urlCache.get(v);
    if (hit && Date.now() - hit.fetchedAt > TTL_MS - 5 * 60 * 1000) {
      void mint(v, true).catch(() => undefined);
    }
  }
});

window.addEventListener('online', () => {
  if (state.error && state.now) {
    state.error = null;
    emit();
  }
});
